import { FC, useState, useCallback } from 'react';
import type {
  QuestionFormBlock as QuestionFormBlockType,
  QuestionFormQuestion,
} from '@/types/blocks';
import { Text } from '@/components/Typography';

interface QuestionFormBlockProps {
  block: QuestionFormBlockType;
  onSubmit?: (prompt: string) => void;
  initialSubmitted?: boolean;
}

type QuestionState = {
  selected: string[];
  textInputs: Record<string, string>;
};

type FormState = Record<string, QuestionState>;

function initState(questions: QuestionFormQuestion[]): FormState {
  const state: FormState = {};
  for (const q of questions) {
    state[q.id] = { selected: [], textInputs: {} };
  }
  return state;
}

function serializeAnswers(questions: QuestionFormQuestion[], state: FormState): string {
  const lines: string[] = [];
  let idx = 0;
  for (const q of questions) {
    const qs = state[q.id];
    if (!qs || qs.selected.length === 0) continue;

    const parts = qs.selected.map((val) => {
      const opt = q.options.find((o) => o.value === val);
      const text = qs.textInputs[val];
      if (opt?.allows_text_input && text) {
        return `${opt.label}: ${text}`;
      }
      return opt?.label ?? val;
    });

    lines.push(`${idx + 1}. ${q.question} — ${parts.join(', ')}`);
    idx++;
  }
  return lines.join('\n');
}

export const QuestionFormBlock: FC<QuestionFormBlockProps> = ({
  block,
  onSubmit,
  initialSubmitted = false,
}) => {
  const [formState, setFormState] = useState<FormState>(() => initState(block.questions));
  const [submitted, setSubmitted] = useState(initialSubmitted);

  const toggleOption = useCallback(
    (questionId: string, value: string, selectionType: string) => {
      setFormState((prev) => {
        const qs = prev[questionId];
        if (!qs) return prev;

        let nextSelected: string[];
        if (selectionType === 'single_choice') {
          nextSelected = qs.selected.includes(value) ? [] : [value];
        } else {
          nextSelected = qs.selected.includes(value)
            ? qs.selected.filter((v) => v !== value)
            : [...qs.selected, value];
        }

        const nextTextInputs = { ...qs.textInputs };
        for (const key of Object.keys(nextTextInputs)) {
          if (!nextSelected.includes(key)) {
            delete nextTextInputs[key];
          }
        }

        return { ...prev, [questionId]: { selected: nextSelected, textInputs: nextTextInputs } };
      });
    },
    [],
  );

  const setTextInput = useCallback((questionId: string, optionValue: string, text: string) => {
    setFormState((prev) => {
      const qs = prev[questionId];
      if (!qs) return prev;
      return {
        ...prev,
        [questionId]: {
          ...qs,
          textInputs: { ...qs.textInputs, [optionValue]: text },
        },
      };
    });
  }, []);

  const allRequiredAnswered = block.questions.every((q) => {
    if (!q.required) return true;
    const qs = formState[q.id];
    return qs && qs.selected.length > 0;
  });

  const handleSubmit = () => {
    if (!allRequiredAnswered || submitted) return;
    const prompt = serializeAnswers(block.questions, formState);
    if (!prompt.trim()) return;
    setSubmitted(true);
    onSubmit?.(prompt);
  };

  return (
    <div>
      {block.title && (
        <Text variant="text-md-bold" className="text-grey-black dark:text-grey-main-dark mb-1">
          {block.title}
        </Text>
      )}
      {block.description && (
        <Text variant="text-sm-regular" className="text-grey-hover dark:text-grey-medium-dark mb-4">
          {block.description}
        </Text>
      )}

      {submitted ? (
        <SubmittedView questions={block.questions} formState={formState} />
      ) : (
        <>
          <div className="flex flex-col gap-5">
            {block.questions.map((q, i) => (
              <QuestionField
                key={q.id}
                index={i + 1}
                question={q}
                state={formState[q.id]}
                disabled={false}
                onToggle={(val) => toggleOption(q.id, val, q.selection_type)}
                onTextChange={(optVal, text) => setTextInput(q.id, optVal, text)}
              />
            ))}
          </div>
          <div className="mt-5">
            <button
              disabled={!allRequiredAnswered}
              onClick={handleSubmit}
              className="px-5 h-8 rounded-lg bg-pif-green text-white text-sm font-medium
                         disabled:opacity-40 disabled:cursor-not-allowed
                         hover:bg-pif-green-light transition-colors"
            >
              Submit answers
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SubmittedView: FC<{ questions: QuestionFormQuestion[]; formState: FormState }> = ({
  questions,
  formState,
}) => {
  const hasAnswers = questions.some((q) => (formState[q.id]?.selected.length ?? 0) > 0);

  if (!hasAnswers) {
    return (
      <div className="flex flex-col gap-3">
        {questions.map((q, i) => (
          <div key={q.id} className="flex items-start gap-2">
            <span className="mt-[1px] shrink-0 text-sm font-semibold text-grey-medium dark:text-grey-medium-dark w-5 text-right">
              {i + 1}.
            </span>
            <p className="text-md font-medium text-grey-black dark:text-grey-main-dark">{q.question}</p>
          </div>
        ))}
        <div className="flex items-center gap-1.5 mt-2 text-sm text-grey-medium dark:text-grey-medium-dark">
          <CheckIcon />
          Answers were submitted
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {questions.map((q, i) => {
        const qs = formState[q.id];
        const selected = qs?.selected ?? [];
        if (selected.length === 0) return null;
        const labels = selected.map((val) => {
          const opt = q.options.find((o) => o.value === val);
          const text = qs?.textInputs[val];
          if (opt?.allows_text_input && text) return `${opt.label}: ${text}`;
          return opt?.label ?? val;
        });
        return (
          <div key={q.id} className="flex items-start gap-2">
            <span className="mt-[1px] shrink-0 text-sm font-semibold text-grey-medium dark:text-grey-medium-dark w-5 text-right">
              {i + 1}.
            </span>
            <div>
              <p className="text-md font-medium text-grey-black dark:text-grey-main-dark">{q.question}</p>
              <p className="text-md text-pif-green font-medium mt-0.5">{labels.join(', ')}</p>
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-1.5 mt-2 text-sm text-grey-medium dark:text-grey-medium-dark">
        <CheckIcon />
        Answers sent
      </div>
    </div>
  );
};

const QuestionField: FC<{
  index: number;
  question: QuestionFormQuestion;
  state: QuestionState | undefined;
  disabled: boolean;
  onToggle: (value: string) => void;
  onTextChange: (optionValue: string, text: string) => void;
}> = ({ index, question, state, disabled, onToggle, onTextChange }) => {
  const isMultiple = question.selection_type === 'multiple_choice';
  const selected = state?.selected ?? [];

  return (
    <div>
      <div className="flex items-start gap-2 mb-2">
        <span className="mt-[1px] shrink-0 text-sm font-semibold text-grey-medium dark:text-grey-medium-dark w-5 text-right">
          {index}.
        </span>
        <Text
          variant="text-md-regular"
          className="text-grey-black dark:text-grey-main-dark font-medium"
        >
          {question.question}
          {question.required && <span className="text-negative-primary ml-0.5">*</span>}
        </Text>
      </div>
      <div className="flex flex-col gap-2 pl-7">
        {question.options.map((opt) => {
          const isSelected = selected.includes(opt.value);

          return (
            <div key={opt.value}>
              <div
                role={isMultiple ? 'checkbox' : 'radio'}
                aria-checked={isSelected}
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : () => onToggle(opt.value)}
                onKeyDown={disabled ? undefined : (e) => {
                  if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(opt.value); }
                }}
                className={`
                  flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors
                  ${disabled ? 'opacity-60 cursor-default' : 'cursor-pointer'}
                  ${isSelected
                    ? 'bg-white dark:bg-grey-dark border border-pif-green'
                    : 'bg-white/60 dark:bg-grey-light-dark border border-transparent hover:bg-white dark:hover:bg-grey-dark'
                  }
                `}
              >
                {isMultiple ? (
                  <span className={`shrink-0 w-4 h-4 rounded flex items-center justify-center transition-colors ${isSelected ? 'bg-pif-green border-2 border-pif-green' : 'border-2 border-grey-medium dark:border-grey-medium-dark bg-transparent'}`}>
                    {isSelected && (
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <polyline points="2 6 5 9 10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                ) : (
                  <span className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-colors ${isSelected ? 'border-2 border-pif-green bg-pif-green' : 'border-2 border-grey-medium dark:border-grey-medium-dark bg-transparent'}`}>
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>
                )}
                <span className="text-md text-grey-black dark:text-grey-main-dark">{opt.label}</span>
              </div>

              {opt.allows_text_input && isSelected && (
                <input
                  type="text"
                  placeholder="Please specify..."
                  value={state?.textInputs[opt.value] ?? ''}
                  disabled={disabled}
                  onChange={(e) => onTextChange(opt.value, e.target.value)}
                  className="mt-1 ml-7 block rounded-md border border-peach-2 dark:border-grey-plain-dark bg-white dark:bg-grey-light-dark px-3 py-1.5 text-sm text-grey-black dark:text-grey-main-dark placeholder:text-grey-medium outline-none focus:border-pif-green"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
